import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';

const AdminLayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: #f0f2f5;
`;

const Sidebar = styled.div`
  width: ${props => props.collapsed ? '64px' : '250px'};
  background: linear-gradient(180deg, #1a1a2e 0%, #16213e 100%);
  box-shadow: 4px 0 15px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  overflow-y: auto;
  z-index: 1000;
  transition: width 0.3s ease;

  @media (max-width: 768px) {
    width: ${props => props.collapsed ? '0' : '250px'};
    transform: translateX(${props => props.collapsed ? '-100%' : '0'});
  }
`;

const SidebarHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Logo = styled.h2`
  font-size: 1.1rem;
  color: #fff;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LogoIcon = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const ToggleButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const SidebarNav = styled.nav`
  flex: 1;
  padding: 1rem 0;
`;

const NavSection = styled.div`
  margin-bottom: 1.5rem;
`;

const NavSectionTitle = styled.div`
  padding: 0.5rem 1rem;
  font-size: 0.65rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NavItem = styled.div`
  padding: 0.875rem 1rem;
  color: ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.7)'};
  background-color: ${props => props.active ? 'rgba(102, 126, 234, 0.15)' : 'transparent'};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  transition: all 0.2s;
  font-weight: ${props => props.active ? '600' : '400'};
  border-left: 3px solid ${props => props.active ? '#667eea' : 'transparent'};
  white-space: nowrap;

  &:hover {
    background-color: rgba(255, 255, 255, 0.08);
    color: #fff;
  }
`;

const NavIcon = styled.span`
  font-size: 1.1rem;
  flex-shrink: 0;
`;

const NavLabel = styled.span`
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
`;

const VersionText = styled.div`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
`;

const MainContent = styled.div`
  flex: 1;
  margin-left: ${props => props.sidebarCollapsed ? '64px' : '250px'};
  min-height: 100vh;
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Header = styled.header`
  background: white;
  padding: 1rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: transparent;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #666;

  @media (max-width: 768px) {
    display: block;
  }
`;

const Breadcrumb = styled.div`
  font-size: 0.875rem;
  color: #666;

  a {
    color: #667eea;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
`;

const SearchBox = styled.div`
  position: relative;

  input {
    padding: 0.5rem 1rem 0.5rem 2.5rem;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    font-size: 0.875rem;
    width: 200px;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
      width: 250px;
    }
  }

  svg {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    width: 14px;
    height: 14px;
  }
`;

const UserMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  transition: background 0.2s;

  &:hover {
    background: #f5f5f5;
  }
`;

const UserAvatar = styled.div`
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
`;

const UserName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #333;
`;

const ContentArea = styled.main`
  padding: 1.5rem 2rem;
`;

const AdminLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const isActive = (path) => location.pathname === path;

  // 当前登录者角色：admin（我司管理人员）或 supplier（供应商）
  const currentUser = (() => {
    try {
      return JSON.parse(localStorage.getItem('adminUser') || '{}');
    } catch (e) {
      return {};
    }
  })();
  const isSupplier = currentUser.role === 'supplier';

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  // 供应商只看得到「我的产品」，其余功能仅我司管理人员可见
  const navItems = isSupplier
    ? [
        {
          section: '产品管理',
          items: [
            { path: '/admin/products', icon: '📦', label: '我的产品' }
          ]
        }
      ]
    : [
    {
      section: '主菜单',
      items: [
        { path: '/admin/dashboard', icon: '📊', label: '仪表盘' },
        { path: '/admin/users', icon: '👥', label: '用户管理' },
        { path: '/admin/points', icon: '⭐', label: '积分管理' },
        { path: '/admin/engineer-verifications', icon: '🛡️', label: '认证审核' },
        { path: '/admin/suppliers', icon: '🏭', label: '供应商管理' },
      ]
    },
    {
      section: '业务管理',
      items: [
        { path: '/admin/history', icon: '💬', label: '客服记录' },
        { path: '/admin/announcements', icon: '📢', label: '公告管理' },
        { path: '/admin/banners', icon: '🖼️', label: '轮播图管理' },
        { path: '/admin/demands', icon: '💡', label: '需求管理' },
        { path: '/admin/documents', icon: '📚', label: '文档管理' },
      ]
    },
    {
      section: '产品管理',
      items: [
        { path: '/admin/products', icon: '📦', label: '产品管理' },
        { path: '/admin/solutions', icon: '🎯', label: '解决方案' },
        { path: '/admin/knowledge', icon: '🧠', label: '知识库' },
      ]
    },
    {
      section: '系统设置',
      items: [
        { path: '/admin/agent-config', icon: '🤖', label: '智能体配置' },
        { path: '/admin/logs', icon: '📋', label: '日志管理' },
        { path: '/admin/service', icon: '🔧', label: '服务监控' },
        { path: '/admin/cdn', icon: '🚀', label: 'CDN 加速' },
        { path: '/admin/settings', icon: '⚙️', label: '配置管理' },
        { path: '/admin/backup', icon: '💾', label: '数据备份' },
      ]
    }
  ];

  return (
    <AdminLayoutContainer>
      <Sidebar collapsed={sidebarCollapsed}>
        <SidebarHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <LogoIcon>HT</LogoIcon>
            {!sidebarCollapsed && <Logo>航泰管理系统</Logo>}
          </div>
          <ToggleButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
            {sidebarCollapsed ? '▶' : '◀'}
          </ToggleButton>
        </SidebarHeader>
        <SidebarNav>
          {navItems.map((section, idx) => (
            <NavSection key={idx}>
              {!sidebarCollapsed && <NavSectionTitle>{section.section}</NavSectionTitle>}
              {section.items.map((item) => (
                <NavItem
                  key={item.path}
                  active={isActive(item.path)}
                  onClick={() => navigate(item.path)}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <NavIcon>{item.icon}</NavIcon>
                  {!sidebarCollapsed && <NavLabel>{item.label}</NavLabel>}
                </NavItem>
              ))}
            </NavSection>
          ))}
        </SidebarNav>
        {!sidebarCollapsed && (
          <SidebarFooter>
            <VersionText>航泰管理系统 v1.0.0</VersionText>
          </SidebarFooter>
        )}
      </Sidebar>
      <MainContent sidebarCollapsed={sidebarCollapsed}>
        <Header>
          <HeaderLeft>
            <MobileMenuButton onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
              ☰
            </MobileMenuButton>
            <Breadcrumb>
              <a href="#!" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }}>仪表盘</a>
              {title && (
                <> / <span style={{ color: '#333' }}>{title}</span></>
              )}
            </Breadcrumb>
          </HeaderLeft>
          <HeaderRight>
            <SearchBox>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input type="text" placeholder="搜索..." />
            </SearchBox>
            <UserMenu onClick={handleLogout} title="退出登录">
              <UserAvatar>A</UserAvatar>
              <UserName>管理员</UserName>
            </UserMenu>
          </HeaderRight>
        </Header>
        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </AdminLayoutContainer>
  );
};

export default AdminLayout;